import { useEffect, useRef, useState } from 'react';
import { createMerchantSecurityClient, securityErrorMessage } from './merchantSecurity';
import { validatePinPolicy, pinCredentials } from './pinPolicy';
import SecurityChallenge from './SecurityChallenge';
import PinDigits from './PinDigits';
import { ShieldCheck, X, ArrowLeft, LockKeyhole } from 'lucide-react';
import './pin-security.css';

export default function PinSecurity({ initialCode, mode = 'login', onComplete, onRestart, onCancel }) {
    const [client] = useState(() => createMerchantSecurityClient());
    const [stage, setStage] = useState('password');
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [pin, setPin] = useState('');
    const [repeat, setRepeat] = useState('');
    const [recovery, setRecovery] = useState('');
    const [management, setManagement] = useState(null);
    const [generation, setGeneration] = useState(0);
    const panel = useRef(null);
    const locked = useRef(false);
    const alive = useRef(true);
    const completed = useRef(null);
    const clear = () => { setStage('password'); setPassword(''); setPin(''); setRepeat(''); setRecovery(''); setManagement(null); setGeneration(n => n + 1); };
    const load = async () => {
        if (locked.current) return;
        locked.current = true; setBusy(true); setError(''); setSettings(null); clear();
        try {
            const data = validatePinPolicy(await client.settings());
            if (!alive.current) return;
            if (completed.current) { onComplete(completed.current); return; }
            setSettings(data);
        } catch (e) { if (alive.current) setError(securityErrorMessage(e)); }
        finally { locked.current = false; if (alive.current) setBusy(false); }
    };
    const loadRef = useRef(load); loadRef.current = load;
    useEffect(() => {
        alive.current = true; void loadRef.current();
        return () => { alive.current = false; client.clear(); };
    }, [client]);
    useEffect(() => {
        if (!onCancel) return;
        const previous = document.activeElement;
        panel.current?.querySelector('button')?.focus();
        return () => previous?.focus?.();
    }, [onCancel]);
    const trapKeys = event => {
        if (!onCancel) return;
        if (event.key === 'Escape' && !locked.current) { event.preventDefault(); onCancel(); }
        if (event.key !== 'Tab') return;
        const nodes = [...panel.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const fail = e => {
        clear(); setError(securityErrorMessage(e));
        if ([4032, 4091, 4230, 9999, 5033].includes(e?.code)) { setSettings(null); client.clear(); }
    };
    const complete = async proof => {
        if (locked.current) return;
        if (proof.expiresAt <= Date.now()) throw new Error('PIN 인증이 만료되었습니다. 다시 인증해주세요.');
        locked.current = true; setBusy(true);
        try {
            const data = await client.post('/login/complete', { authorization: proof.value });
            if (!data?.user) throw new Error('로그인 사용자 정보를 확인하지 못했습니다. 다시 로그인해주세요.');
            completed.current = data;
            validatePinPolicy(await client.settings());
            if (alive.current) onComplete(data);
        } catch (e) { if (alive.current) { setSettings(null); fail(e); } }
        finally { locked.current = false; if (alive.current) setBusy(false); }
    };
    const save = async e => {
        e.preventDefault();
        if (locked.current || !settings?.enabled) return;
        if (!settings.recovery_required && Number(settings.locked_until) > Date.now()) return;
        let body;
        try { body = pinCredentials({ settings, password, pin, repeat, management, recovery }); }
        catch (failure) { if (management && management.expiresAt <= Date.now()) clear(); setError(failure.message); return; }
        locked.current = true; setBusy(true); setError('');
        try {
            await client.post('/credentials', body);
            clear(); client.clear();
            if (alive.current) onRestart('PIN을 저장했습니다. 아이디와 비밀번호로 다시 로그인해주세요.');
        } catch (failure) { if (alive.current) fail(failure); }
        finally { locked.current = false; if (alive.current) setBusy(false); }
    };
    const recoveryRequired = settings?.recovery_required === true;
    const timedLock = Number(settings?.locked_until) > Date.now();
    const editing = mode === 'manage' || initialCode === 4281 || settings?.pin_registered === false || recoveryRequired;
    const needsManagement = editing && settings?.pin_registered && !recoveryRequired && !management;
    const title = recoveryRequired ? 'PIN 복구' : editing ? (settings?.pin_registered === false ? 'PIN 최초 등록' : 'PIN 변경') : '로그인 PIN 인증';
    const leave = () => { clear(); client.clear(); if (onCancel) onCancel(); else onRestart(''); };
    return <section ref={panel} onKeyDown={trapKeys} className={`pin-ui ${editing && stage === 'pin' ? 'pin-final-stage' : ''}`} aria-busy={busy}>
        {onCancel && <button className="pin-close" type="button" aria-label="닫기" disabled={busy} onClick={leave}><X size={20} /></button>}
        <header className="pin-heading">
            <span className="pin-emblem"><ShieldCheck size={28} strokeWidth={1.7} /></span>
            <p className="pin-eyebrow">가맹점 보안 인증</p>
            <h2>{title}</h2>
            <p className="pin-description">{editing ? '로그인과 지갑 이용을 안전하게 보호합니다.' : '등록한 4자리 PIN을 입력해주세요.'}</p>
        </header>
        {busy && <p className="pin-loading" role="status">인증 정보를 확인하고 있습니다…</p>}
        {error && <p role="alert" className="pin-error">{error}</p>}
        {settings?.enabled === false && <p className="pin-note" role="status">PIN 인증 기능이 비활성화되어 있습니다.</p>}
        {timedLock && !recoveryRequired && <p className="pin-error" role="alert">인증 잠금 해제 예정: {new Date(settings.locked_until).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}</p>}
        {settings?.enabled && (!timedLock || recoveryRequired) && !busy && <>
            {editing ? <>
                <ol className="pin-steps" aria-label="PIN 등록 단계">
                    {settings.pin_registered && !recoveryRequired && <li className={needsManagement ? 'active' : 'done'} aria-current={needsManagement ? 'step' : undefined}>기존 PIN 확인</li>}
                    <li className={!needsManagement && stage === 'password' ? 'active' : stage === 'pin' ? 'done' : ''} aria-current={!needsManagement && stage === 'password' ? 'step' : undefined}>비밀번호 입력</li>
                    <li className={stage === 'pin' ? 'active' : ''} aria-current={stage === 'pin' ? 'step' : undefined}>새 PIN 등록</li>
                </ol>
                {needsManagement ? <>
                    <p className="pin-section-help">PIN을 변경하려면 기존 PIN을 먼저 확인해주세요.</p>
                    <SecurityChallenge key={`manage:${generation}`} client={client} purpose="manage" method="PIN" polished autoStart submitLabel="기존 PIN 확인" onVerified={setManagement} onSessionError={fail} />
                </> : stage === 'password' ? <form onSubmit={e => { e.preventDefault(); if (password.trim() && (!recoveryRequired || recovery.trim())) { setError(''); setStage('pin'); } }}>
                    {recoveryRequired && <><p className="pin-section-help">관리자에게 받은 복구 권한을 입력해주세요.</p><label className="pin-field">복구 권한<input type="password" autoComplete="off" value={recovery} onChange={e => setRecovery(e.target.value)} /></label></>}
                    <label className="pin-field">현재 로그인 비밀번호<input type="password" autoFocus autoComplete="off" placeholder="로그인에 사용하는 비밀번호" value={password} onChange={e => setPassword(e.target.value)} /></label>
                    <p className="pin-section-help">PIN 저장 시 로그인 비밀번호를 함께 확인합니다.</p>
                    <button className="pin-primary" type="submit" disabled={!password.trim() || (recoveryRequired && !recovery.trim())}>다음</button>
                </form> : <form onSubmit={save}>
                    <PinDigits label="새 PIN 4자리" value={pin} onChange={setPin} autoFocus />
                    <PinDigits label="새 PIN 확인" value={repeat} onChange={setRepeat} />
                    <div className="pin-note"><LockKeyhole size={16} /><p>반복·연속 숫자는 사용할 수 없습니다.<br />저장 후 다시 로그인해주세요.</p></div>
                    <button type="submit" className="pin-primary" disabled={pin.length !== 4 || repeat.length !== 4}>PIN 저장</button>
                    <button type="button" className="pin-back" onClick={() => { setPin(''); setRepeat(''); setError(''); setStage('password'); }}><ArrowLeft size={15} />이전 단계</button>
                </form>}
            </> : settings.login.required ? <SecurityChallenge key={`login:${generation}`} client={client} purpose="login" method="PIN" polished autoStart onVerified={complete} onSessionError={fail} />
                : <p className="pin-note">로그인 PIN 인증이 필요하지 않습니다. 로그인 화면에서 다시 로그인해주세요.</p>}
        </>}
        {error && <button className="pin-back" disabled={busy} onClick={load}>인증 설정 다시 확인</button>}
        {!onCancel && !(editing && stage === 'pin') && <footer className="pin-footer"><button type="button" disabled={busy} onClick={leave}><ArrowLeft size={15} />로그인 화면으로</button></footer>}
    </section>;
}
